import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import { api } from "@/lib/api";
import { formatINR } from "@/lib/utils";
import LoadingScreen from "@/components/LoadingScreen";

export default function PricingPage() {
  const [plans, setPlans] = useState(null);

  useEffect(() => {
    api.get("/plans").then(({ data }) => setPlans(data)).catch(() => setPlans([]));
  }, []);

  if (!plans) return <LoadingScreen />;

  return (
    <div data-testid="pricing-page">
      <section className="sqit-section">
        <div className="sqit-container">
          <div className="max-w-3xl">
            <div className="text-xs font-semibold uppercase tracking-widest text-[#538135]">PRICING</div>
            <h1 className="mt-3 font-display text-4xl sm:text-5xl font-bold text-gray-900 tracking-tight">
              Credit packs, sized for how you use.
            </h1>
            <p className="mt-5 text-lg text-gray-600">
              Buy credits once, use them across any module. Credits never expire.
              Unused credits from failed or rejected jobs are refunded automatically.
            </p>
          </div>

          <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
            {plans.map((p) => (
              <div
                key={p.id}
                data-testid={`plan-card-${p.name.toLowerCase()}`}
                className={`sqit-card p-8 flex flex-col ${p.highlighted ? "border-[#538135] ring-2 ring-[#538135]/20" : ""}`}
              >
                {p.highlighted && (
                  <div className="inline-block self-start px-3 py-1 rounded-full bg-[#EAF4E3] text-[#09582D] text-[11px] font-semibold tracking-wide mb-4">
                    MOST POPULAR
                  </div>
                )}
                <h3 className="font-display text-2xl font-semibold text-gray-900">{p.name}</h3>
                <p className="mt-1 text-sm text-gray-500">{p.tagline}</p>
                <div className="mt-6 flex items-baseline gap-1">
                  <span className="font-display text-4xl font-bold text-gray-900">{formatINR(p.price_inr)}</span>
                </div>
                <div className="mt-1 text-xs text-gray-500">
                  {p.credits} credits · {formatINR(p.price_inr / p.credits)} / credit
                </div>
                <ul className="mt-6 space-y-3 flex-1">
                  {(p.features || []).map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                      <Check size={16} className="text-[#538135] mt-0.5 flex-shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/app/credits"
                  data-testid={`plan-cta-${p.name.toLowerCase()}`}
                  className={`mt-8 text-center rounded-md px-4 py-3 text-sm font-semibold transition-colors ${p.highlighted
                      ? "bg-[#538135] text-white hover:bg-[#09582D]"
                      : "bg-white border border-gray-300 text-gray-900 hover:bg-gray-50"
                    }`}
                >
                  Start with {p.name}
                </Link>
              </div>
            ))}
          </div>

          <div className="mt-8 sqit-card p-8 bg-white border border-[#E5E7EB]">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div>
                <div className="text-xs font-semibold uppercase tracking-widest text-[#538135]">
                  ABOUT CREDITS
                </div>

                <h3 className="mt-2 font-display text-xl font-semibold text-gray-900">
                  Credits are used to pay for bioinformatics analyses.
                </h3>

                <p className="mt-3 text-sm text-gray-600 leading-relaxed">
                  Each analysis consumes credits based on the selected pipeline and the
                  number of samples processed. Credits remain in your account until used,
                  do not expire, and can be purchased whenever additional analyses are
                  required.
                </p>
              </div>

              <div className="rounded-lg bg-[#F8FAF7] border border-[#E5E7EB] p-5">
                <div className="text-sm font-semibold text-gray-900">
                  Credit Benefits
                </div>

                <ul className="mt-4 space-y-3 text-sm text-gray-600">
                  <li className="flex items-start gap-2">
                    <Check size={16} className="text-[#538135] mt-0.5 flex-shrink-0" />
                    Purchase credits only when you need them.
                  </li>

                  <li className="flex items-start gap-2">
                    <Check size={16} className="text-[#538135] mt-0.5 flex-shrink-0" />
                    Credits never expire.
                  </li>

                  <li className="flex items-start gap-2">
                    <Check size={16} className="text-[#538135] mt-0.5 flex-shrink-0" />
                    Use the same credit balance across all supported analysis modules.
                  </li>

                  <li className="flex items-start gap-2">
                    <Check size={16} className="text-[#538135] mt-0.5 flex-shrink-0" />
                    Easily top up your account whenever additional analyses are required.
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="mt-14 sqit-card p-8 bg-[#F8FAF7]">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h3 className="font-display text-xl font-semibold text-gray-900">Need something custom?</h3>
                <p className="text-sm text-gray-600 mt-1 max-w-xl">
                  Custom credit packs are available for volume purchases, purchase orders, or NDA-covered projects.
                </p>
              </div>
              <Link
                to="/contact"
                className="inline-block bg-[#538135] text-white hover:bg-[#09582D] transition-colors rounded-md px-5 py-3 text-sm font-semibold"
              >
                Contact sales
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
